import React, { Component } from 'react'
import { isEmail } from 'validator'
import InlineSvg from 'svg-inline-react'
import InviteSelect from 'components/shared/InviteSelect'
import EditUserRole from '../EditUserRole'
import IntegrationLayout from 'components/Integration/PageLayout'
import ACCESS, { Gates } from 'utils/Access'
import TeamTableBody from '../TeamTable/TeamTableBody'
import * as TeamConstants from '../TeamConstants'
import { SeatUpgradeModalButton, SeatUpgradeWarning, SeatBillingWarning } from '@driftt/billing-components'
import { defaultTo, isEmpty } from 'ramda'
import { refreshCurrentUser } from 'app/modules/user/actions'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import withNotifications from 'components/shared/notifications'
import { checkEmailIsSSOEnabled } from 'app/modules/auth/api'
import './style.styl'

const closeSRC = require('!svg-inline?removeTags=true!images/navigation/close.svg')
const USER_EXISTENCE_DEBOUNCE_IN_MS = 200
const ERROR_MESSAGES = {
  USER_EXISTS_IN_CURRENT_ORG: email => `${email} is already a member of your team.`,
  SSO_ENABLED_DOMAIN: email => `${email} is set up for Single Sign On. Have an IT admin add them to the Drift app in their Identity Provider.`,
  USER_EXISTS_IN_OTHER_ORG: email => `${email} is already a member of another organization.`,
}

const defaultToEmptyArray = defaultTo([])
const isArrayEmpty = (arr) => isEmpty(defaultToEmptyArray(arr))


class InviteTeam extends Component {
  constructor(props) {
    super(props)
    this.state = this.initialState()
  }

  initialState() {
    const hasSlackUsers = !!this.props.users.size
    const defaultOptions = hasSlackUsers
      ? this.props.users.toJS().map(user => {
        return { value: user.email, label: user.name }
      })
      : []
    return {
      showPermissions: false,
      showSuccess: false,
      hasSlack: hasSlackUsers,
      users: this.props.users,
      selectedUsers: [],
      defaultOptions: [], //dont use slack users until we page
      options: [],
      updatedScopes: [],
      inviteCount: 0,
      inviteScopes: []
    }
  }

  restartInviteProcess = () => this.setState(this.initialState(), this.handleBilling)

  componentWillReceiveProps(props) {
    const hasSlackUsers = !!props.users.size
    const defaultOptions = hasSlackUsers
      ? props.users.toJS().map(user => {
        return { value: user.email, label: user.name }
      })
      : []

    this.setState({
      hasSlack: hasSlackUsers,
      defaultOptions: [],
      users: props.users,
    })
  }

  submitChanges = (updatedScopes, users) => {
    const selectedEmails = _.values(users).map(user => user.email)
    const slackUsersToInvite = this.props.users.toJS().filter(user => selectedEmails.includes(user.email))
    const emailUsersToInvite = selectedEmails.filter(
      email => !_.filter(slackUsersToInvite, { email: email }).length,
    )

    slackUsersToInvite.length && this.props.onInviteSlackUsers(slackUsersToInvite, updatedScopes)
    emailUsersToInvite.length && this.props.onInviteEmailUsers(emailUsersToInvite, updatedScopes)

    this.setState({ showSuccess: true, showPermissions: false, updatedScopes })
  }

  handleOnChange = value => {
    if (!value) return
    if (this.checkExistingUser(value)) return

    const userInputUsers = value.split(' ').filter(isEmail).map(inputValue => {
      return { label: inputValue, value: inputValue }
    })

    this.setState(prevState => {

      return {
        options: [...userInputUsers, ...prevState.options],
      }
    })

    this.checkUserInOtherOrg(value)
    this.checkSSODomain(value)
  }

  debounceHandleOnChange = _.debounce(this.handleOnChange, USER_EXISTENCE_DEBOUNCE_IN_MS)

  handleOnEnterValues = event => {
    switch (event.keyCode) {
      case 13: // ENTER
        if (!this.validateOptionsAsEmail() || this.checkExistingUser()) {
          event.preventDefault()
          break
        }

        this.setState(prevState => {
          return {
            defaultOptions: [...prevState.defaultOptions, ...prevState.options],
          }
        })
        break
    }
  }

  handleInputUpdate = selectedUsers => {
    this.setState({
      inviteCount: selectedUsers.length,
      selectedUsers: selectedUsers.map(entry => entry.value)
    })
  }

  handleInvite = ({ hasActiveSubscription }) => {
    const { selectedUsers } = this.state

    const onStateSet = () => {
      hasActiveSubscription
        ? this.togglePermissions(selectedUsers)
        : this.skipPermissionsStep(selectedUsers)
    }

    this.setState({
      showPermissions: hasActiveSubscription,
      hasActiveSubscription
    }, onStateSet)
  }

  skipPermissionsStep = selectedUsers => {
    this.setState({ selectedUsers },
      () => this.submitChanges([TeamConstants.requiredScopes.MEMBER, TeamConstants.permissionScopes.ADMIN], this.formatSelectedUsers())
    )
  }

  togglePermissions = selectedUsers => {
    this.setState(prevState => {
      return {
        selectedUsers: Array.isArray(selectedUsers) ? selectedUsers : prevState.selectedUsers
      }
    })
  }

  dismissPermissionsView = () => {
    this.setState({ showPermissions: false })
  }

  resetInitialState = () => {
    this.setState(this.initialState())
  }

  validateOptionsAsEmail() {
    const hasInvalid = this.state.options.some(option => {
      return !isEmail(option.value)
    })

    return !hasInvalid
  }

  checkExistingUser(value) {
    const options = !!value ? [{ value: value }] : this.state.options
    const userExists = options.some(option => {
      const existing = _.filter(this.props.orgUsers, { email: option.value })
      if (existing.length) {
        this.props.sendNotification(ERROR_MESSAGES.USER_EXISTS_IN_CURRENT_ORG(existing[0].email))
        return existing
      }
    })

    return userExists
  }

  async checkSSODomain(value) {
    const onDone = (data) => {
      if (data.canLogIn) {
        this.props.sendNotification(ERROR_MESSAGES.SSO_ENABLED_DOMAIN(value))
        this.setState(prevState => {
          return {
            options: _.reject(prevState.options, { value: value }),
          }
        })
      }
    }
    checkEmailIsSSOEnabled({ email: value })
      .then(onDone)
  }

  async checkUserInOtherOrg(value) {
    const isTaken = await this.props.checkEmailInOtherOrgs(value)

    if (isTaken) {
      this.props.sendNotification(ERROR_MESSAGES.USER_EXISTS_IN_OTHER_ORG(value))
      this.setState(prevState => {
        return {
          options: _.reject(prevState.options, { value: value }),
        }
      })
    }

    return isTaken
  }

  formatSelectedUsers() {
    const { updatedScopes } = this.state
    const formattedUsers = this.state.selectedUsers.map(email => {
      const slackUser = _.filter(this.props.users.toJS(), { email: email })[0] || {}

      return {
        email,
        name: slackUser.name || null,
        avatarUrl: slackUser.avatarUrl || null,
        scopes: updatedScopes
      }
    })

    return formattedUsers
  }

  renderInviteSearch() {
    const { inviteCount } = this.state

    const {
      shouldSelectAutofocus,
      showInviteTips,
      notifications
    } = this.props

    const inviteSelectOptions = _.take([...this.state.defaultOptions, ...this.state.options], 50)
    const buttonLabel = ACCESS.hasPaidGates() ? 'Next' : 'Invite'

    return (
      <div>
        <header>
          <div className="manage-team-header">
            <h2>Invite others to join your team</h2>
            <p>You can invite teammates by entering their email address in the field below.</p>
          </div>
          <p className="invite-team-microcopy">💡 Pro-tip: If you have the <a href="https://help.drift.com/article/slack-integration-respond-to-chats-right-from-slack/">Slack integration</a>,
              you can invite teammates directly from Slack!
          </p>
        </header>

        <SeatUpgradeWarning
          className="invite-select-seat-upgrade-warning"
          hasPremiumGates={ACCESS.hasPaidGates()}
          hasUnlimitedSeats={ACCESS.hasGates(Gates.UNLIMITED_SEATS)}
        />

        <div className="invite-team-select-wrapper">
          <InviteSelect
            options={inviteSelectOptions}
            users={this.state.users}
            showInviteTips={showInviteTips}
            shouldSelectAutofocus={shouldSelectAutofocus}
            onChange={this.handleInputUpdate}
            value={this.state.selectedUsers}
            selectOptions={{
              onInputChange: this.debounceHandleOnChange,
              onInputKeyDown: this.handleOnEnterValues,
            }}
          />
          <SeatUpgradeModalButton
            className="invite-team-submit-button"
            onProceed={this.handleInvite}
            label={buttonLabel}
            disabled={inviteCount === 0}
            showErrorNotification={notifications.error}
            newSeatsCount={inviteCount}
            onSubscriptionUpdated={this.refreshGates}
          />
        </div>

        {!this.state.hasSlack && isArrayEmpty(inviteSelectOptions) &&
          <div className="invite-team-slack">
            <IntegrationLayout params={{ integrationId: 'slack' }} />
          </div>
        }

      </div>
    )
  }

  refreshGates = () => {
    this.props.actions.refreshCurrentUser()
  }

  renderPermissionsStep() {
    const { selectedUsers, hasActiveSubscription } = this.state

    return (
      <div>
        <header>
          <div className="manage-team-header no-border">
            <h2>{`Set permissions for ${selectedUsers.length > 1 ? 'new team members' : selectedUsers[0]}`}</h2>
          </div>
        </header>

        <EditUserRole
          users={this.formatSelectedUsers()}
          nextCopy={`Invite ${selectedUsers.length} ${selectedUsers.length > 1 ? 'People' : 'Person'}`}
          cancelCopy="Back"
          onNext={this.submitChanges}
          onClose={this.dismissPermissionsView}
          className="invite-permissions"
          initialScopes={[TeamConstants.permissionScopes.ADMIN, TeamConstants.emailScopes.SALES]}
          warning={
            <SeatBillingWarning
              hasUnlimitedSeats={ACCESS.hasGates(Gates.UNLIMITED_SEATS)}
              newSeatsCount={selectedUsers.length}
            />
          }
          hasPremiumDrift={hasActiveSubscription}
        />
      </div>
    )
  }

  renderSuccess() {
    const seatCopy = this.state.selectedUsers.length > 1 ? 'seats' : 'seat'
    const { successCtaCopy } = this.props
    const { hasActiveSubscription } = this.state

    return (
      <div className="invite-team-success">
        <header>
          <div className="manage-team-header no-border">
            <h2>Your invitation has been sent!</h2>
            <p>You&rsquo;ve added {this.state.selectedUsers.length} {seatCopy} to your team.</p>
          </div>
        </header>

        <table className="drift-table">
          <TeamTableBody
            users={this.formatSelectedUsers()}
            showAccessColumn={hasActiveSubscription}
            showPermissionsColumn={hasActiveSubscription}
          />
        </table>

        <footer>
          {successCtaCopy &&
            <button
              className="drift-button drift-button-tertiary"
              onClick={() => this.props.onClose(this.state.selectedUsers)}>
              {successCtaCopy}
            </button>
          }

          <button className="drift-button drift-button-primary" onClick={this.restartInviteProcess}>
            Invite More People
          </button>
        </footer>
      </div>
    )
  }

  render() {
    const { onClose } = this.props

    return (
      <div className="invite-team">
        <div>
          {!!onClose && <InlineSvg className="close-button" onClick={onClose} src={closeSRC} />}
          {!this.state.showPermissions && !this.state.showSuccess && this.renderInviteSearch()}
          {this.state.showPermissions && this.renderPermissionsStep()}
          {this.state.showSuccess && this.renderSuccess()}
        </div>
      </div>
    )
  }
}

const mapStateToProps = null
const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({ refreshCurrentUser }, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(
  withNotifications(InviteTeam)
)



// WEBPACK FOOTER //
// ./src/scripts/components/settings/team/InviteTeam/index.js